// src/features/tidsregistering/pages/AbsencePlannerPage.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Alert, CircularProgress, Chip, Badge, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { format, subMonths, addMonths } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { AbsencePlannerChart } from '../components/absence-planner';
import CreateAbsenceDialog from '../components/absence-planner/CreateAbsenceDialog';
import AbsenceDetailDialog from '../components/absence-planner/AbsenceDetailDialog';
import { absencesToCategories } from '../adapters/absencePlannerAdapter';
import { useAbsences } from '../hooks/useAbsences';
import { useRegistrationCodes } from '../hooks/useRegistrationCodes';
import { useStaff } from '../../staff/hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useUnavailability } from '../hooks/useUnavailability';
import { useAuth } from '../../../context/AuthContext';
import {
  useTidsregistreringAbsencePermissions,
  useTidsregistreringRegistrationCodePermissions,
} from '../../auth/hooks/usePermissions';

import PageHeader from '../../../components/common/PageHeader';
import EventBusyIcon from '@mui/icons-material/EventBusy';

export default function AbsencePlannerPage() {
  const { t } = useTranslation(['tidsregistering']);
  const { showError, showSuccess } = useSnackbar();

  const { absences, fetchForDepartment, createAbsence, approveAbsence, rejectAbsence, deleteAbsence, loading: absencesLoading, error: absencesError } = useAbsences();
  const { registrationCodes, fetchForDepartment: fetchCodes, loading: codesLoading } = useRegistrationCodes();
  const { staff, fetchDepartmentStaff, loading: staffLoading } = useStaff();
  const { blocks: unavailabilityBlocks, fetchDepartmentUnavailability } = useUnavailability();

  const [baseDate] = useState(new Date());

  const { canCreate: canCreateAbsence } = useTidsregistreringAbsencePermissions();
  const { canRead: canReadCodes } = useTidsregistreringRegistrationCodePermissions();
  // Asking for absence means picking its registration code
  const canRequest = canCreateAbsence && canReadCodes;

  // Detail/approval dialog state
  const [detailSlot, setDetailSlot] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const staffMap = Object.fromEntries((staff ?? []).map((s) => [s.staff_member_id, s]));
  const { user } = useAuth();
  const currentStaffMember = staffMap[user?.staff_member_id] ?? null;

  const handleAbsenceClick = useCallback((slot) => {
    setDetailSlot(slot);
  }, []);

  const handleApprove = useCallback(async (absenceId) => {
    setActionLoading(true);
    const result = await approveAbsence(absenceId, {});
    setActionLoading(false);
    if (result.success) {
      showSuccess('Fravær godkendt');
      setDetailSlot(null);
      await loadAbsences(null, null);
    } else {
      showError(result.error ?? 'Kunne ikke godkende');
    }
  }, [approveAbsence, showSuccess, showError]);

  const handleReject = useCallback(async (absenceId, data) => {
    setActionLoading(true);
    const result = await rejectAbsence(absenceId, data);
    setActionLoading(false);
    if (result.success) {
      showSuccess('Fravær afvist');
      setDetailSlot(null);
      await loadAbsences(null, null);
    } else {
      showError(result.error ?? 'Kunne ikke afvise');
    }
  }, [rejectAbsence, showSuccess, showError]);

  const handleDelete = useCallback(async (absenceId) => {
    setActionLoading(true);
    const result = await deleteAbsence(absenceId);
    setActionLoading(false);
    if (result.success) {
      showSuccess('Fravær slettet');
      setDetailSlot(null);
      await loadAbsences(null, null);
    } else {
      showError(result.error ?? 'Kunne ikke slette');
    }
  }, [deleteAbsence, showSuccess, showError]);

  // Create dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitial, setDialogInitial] = useState(null); // { staffMember, startDate, endDate }
  const [createLoading, setCreateLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const loadAbsences = useCallback(async (startDate, endDate) => {
    const start = format(startDate ?? subMonths(baseDate, 1), 'yyyy-MM-dd');
    const end = format(endDate ?? addMonths(baseDate, 3), 'yyyy-MM-dd');
    const [result] = await Promise.all([
      fetchForDepartment(start, end),
      fetchDepartmentUnavailability(start, end).catch(() => {}),
    ]);
    if (!result.success) {
      showError(t('tidsregistering:absence_planner.failed_to_load'));
    }
  }, [baseDate, fetchForDepartment, fetchDepartmentUnavailability, showError, t]);

  useEffect(() => {
    const init = async () => {
      await Promise.all([
        loadAbsences(null, null),
        canReadCodes ? fetchCodes() : null,
        fetchDepartmentStaff(),
      ]);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [showPendingOnly, setShowPendingOnly] = useState(false);

  const categories = useMemo(
    () => absencesToCategories(absences, staff, registrationCodes),
    [absences, staff, registrationCodes],
  );

  const pendingCount = useMemo(
    () => absences.filter((a) => a.approval_state === 'approval_wish').length,
    [absences],
  );

  const filteredCategories = useMemo(() => {
    if (!showPendingOnly) return categories;
    return categories
      .map((cat) => ({ ...cat, tasks: cat.tasks.filter((t) => t.approvalState === 'approval_wish') }))
      .filter((cat) => cat.tasks.length > 0);
  }, [categories, showPendingOnly]);

  // Opens the create dialog pre-filled from a drag gesture
  const handleDragCreate = useCallback(({ category, startDate, endDate }) => {
    setDialogInitial({
      staffMember: category.staffMember,
      startDate,
      endDate,
    });
    setDialogOpen(true);
  }, []);

  // Button alternative to dragging across the chart, which isn't discoverable
  const handleRequestAbsence = useCallback(() => {
    const today = new Date();
    setDialogInitial({ staffMember: currentStaffMember, startDate: today, endDate: today });
    setDialogOpen(true);
  }, [currentStaffMember]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setDialogInitial(null);
    setSubmitError(null);
  }, []);

  const handleCreateSubmit = useCallback(async (payload) => {
    setSubmitError(null);
    setCreateLoading(true);
    const result = await createAbsence(payload);
    setCreateLoading(false);

    if (result.success) {
      showSuccess(t('tidsregistering:absence_planner.created'));
      handleDialogClose();
      await loadAbsences(null, null);
    } else {
      // result.error is already the extracted message string from useApi
      setSubmitError(result.error ?? t('tidsregistering:absence_planner.create_failed'));
    }
  }, [createAbsence, showSuccess, handleDialogClose, loadAbsences, t]);

  const loading = absencesLoading || codesLoading || staffLoading;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        mb={4}
        icon={EventBusyIcon}
        title={t('tidsregistering:absence_planner.title')}
        subtitle={t('tidsregistering:absence_planner.drag_hint')}
        actions={<>
          <Badge badgeContent={pendingCount || null} color="warning">
            <Chip
              label={t('tidsregistering:absence_planner.show_pending_only')}
              onClick={() => setShowPendingOnly((v) => !v)}
              color={showPendingOnly ? 'warning' : 'default'}
              variant={showPendingOnly ? 'filled' : 'outlined'}
            />
          </Badge>
          {currentStaffMember && canRequest && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleRequestAbsence}>
              {t('tidsregistering:absence_planner.request')}
            </Button>
          )}
        </>}
      />

      {absencesError && <Alert severity="error">{absencesError}</Alert>}

      {loading && categories.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <AbsencePlannerChart
          categories={filteredCategories}
          baseDate={baseDate}
          onDateRangeChange={loadAbsences}
          onDragCreate={canRequest ? handleDragCreate : null}
          onAbsenceClick={handleAbsenceClick}
          height="calc(100vh - 160px)"
          unavailabilityBlocks={unavailabilityBlocks}
        />
      )}

      <AbsenceDetailDialog
        open={!!detailSlot}
        slot={detailSlot}
        staffMember={detailSlot ? staffMap[detailSlot.originalAbsence?.staff_member_id] : null}
        onClose={() => setDetailSlot(null)}
        onApprove={handleApprove}
        onReject={handleReject}
        onDelete={handleDelete}
        loading={actionLoading}
      />

      <CreateAbsenceDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSubmit={handleCreateSubmit}
        staffMember={dialogInitial?.staffMember ?? null}
        initialStartDate={dialogInitial?.startDate ?? null}
        initialEndDate={dialogInitial?.endDate ?? null}
        registrationCodes={registrationCodes}
        loading={createLoading}
        submitError={submitError}
      />
    </Box>
  );
}
